import { useState, useEffect } from "react";
import { TimeSlot, BookingLanguage, FastBookingSlots } from "@/types/booking";
import { getBookableTimesForDate } from "@/utils/bookableSlots";
import TimeSlotsDisplay from "./booking/TimeSlotsDisplay";
import { addHours, isBefore } from "date-fns";
import { useClinics } from "@/hooks/useClinics";
import { getFastBookingSlots } from "@/utils/fastBookingUtils";
import {
  useClinicExternalSlots,
  getExternalSlotsForDate,
} from "@/hooks/useClinicExternalSlots";
import { useToast } from "@/hooks/use-toast";
import { t as translate } from "@/locales";
import { scrollToTopDelayed } from "@/utils/scrollUtils";
import { ExpandableOrderSummary } from "./booking/ExpandableOrderSummary";
import { Product } from "@/components/product-step/types";
import type { SlotSelectionUpdateOptions } from "@/hooks/booking/useTimeSlots";
import { useBookingFlowStore } from "@/stores/bookingFlowStore";
import { useMembership } from "@/contexts/MembershipContext";

interface TimeSlotGridProps {
  date: Date;
  onTimeSelect: (times: string[], options?: SlotSelectionUpdateOptions) => void;
  bookingType: "request"; // Used as an initial value, can change with fast booking
  setBookingType: (type: "request") => void;
  language: BookingLanguage;
  selectedTimeSlots: TimeSlot[];
  onDeleteTimeSlot: (slot: TimeSlot) => void;
  setCurrentStep: (
    step: "details" | "product" | "addons" | "clinic" | "date",
  ) => void;
  selectedClinic?: string;
  packageComponentMap?: Record<string, string[]>;
  onBack?: () => void;
  canGoBack?: boolean;
  hideTitle?: boolean; // Hide title when used within SlotPreferenceBlock
  // New props for order summary
  selectedProducts?: string[];
  selectedAddOns?: string[];
  selectedProductDetails?: Product[];
  selectedAddOnDetails?: Product[];
  totalPrice?: number;
  onProductRemove?: (productId: string) => void;
  onAddOnRemove?: (addOnId: string) => void;
  preference?: "first" | "second";
  allowSelectionOverride?: boolean;
  // Show slot availability indicators (instant vs request)
  showSlotIndicators?: boolean;
  // When true, only show external slots (no opening hours fallback/combination)
  useExternalSlotsOnly?: boolean;
}

const TimeSlotGrid = ({
  date,
  onTimeSelect,
  language,
  selectedTimeSlots,
  setCurrentStep,
  selectedClinic,
  onBack,
  canGoBack = true,
  hideTitle = false,
  // New props for order summary
  selectedProducts = [],
  selectedAddOns = [],
  selectedProductDetails = [],
  selectedAddOnDetails = [],
  totalPrice = 0,
  onProductRemove,
  onAddOnRemove,
  preference,
  allowSelectionOverride = false,
  showSlotIndicators = false,
}: TimeSlotGridProps) => {
  const [selectedTimes, setSelectedTimes] = useState<string[]>([]);
  const { data: clinics, isLoading: clinicsLoading } = useClinics();
  const currentClinic = clinics?.find((c) => c.id === selectedClinic);
  const { toast } = useToast();
  const { getEffectivePrice } = useMembership();

  // Fetch external slots for this clinic (e.g., from OneDoc)
  const { data: externalSlots } = useClinicExternalSlots(selectedClinic);

  // Booking mode - in request mode external slots are ignored for display but
  // still used to detect whether a slot came from the external system.
  const bookingMode = useBookingFlowStore((state) => state.bookingMode);

  // Extract fast booking slots for this clinic
  const fastBookingSlots = currentClinic?.fast_booking_slots as
    | FastBookingSlots
    | undefined;

  useEffect(() => {
    // Only one slot per day - find the slot for the current date
    const slotForCurrentDate = selectedTimeSlots.find(
      (slot) =>
        slot.date.getDate() === date.getDate() &&
        slot.date.getMonth() === date.getMonth() &&
        slot.date.getFullYear() === date.getFullYear(),
    );

    setSelectedTimes(slotForCurrentDate ? [slotForCurrentDate.time] : []);
  }, [date, selectedTimeSlots]);

  // Raw external slots (ignoring bookingMode) — used to detect whether a
  // selected time is an instant (external) slot.
  const rawExternalSlotsForDate = getExternalSlotsForDate(externalSlots, date);

  // Available slots — the single source of truth shared with the persisted-slot
  // validator (_validateTimeSlotsForClinic), so the validator can never delete a
  // slot this picker just offered. Handles abundant / sparse-combine / opening-hours.
  const availableSlots = getBookableTimesForDate(
    currentClinic,
    externalSlots,
    date,
    bookingMode,
  );

  // Filter out slots within 24 hours
  const now = new Date();
  const twentyFourHoursFromNow = addHours(now, 24);

  const displayedSlots = availableSlots.filter((time) => {
    const [hours, minutes] = time.split(":").map(Number);
    const slotDate = new Date(date);
    slotDate.setHours(hours, minutes, 0, 0);
    return !isBefore(slotDate, twentyFourHoursFromNow);
  });

  // Identify fast booking slots
  const fastSlots = getFastBookingSlots(date, fastBookingSlots);

  // If all slots are filtered out by 24-hour check, fall back to availableSlots
  const slotsToDisplay =
    displayedSlots.length > 0 ? displayedSlots : availableSlots;

  const handleTimeSelect = (time: string) => {
    let newSelectedTimes: string[];

    if (selectedTimes.includes(time)) {
      // Deselect the time - only one slot per day so clear the selection
      newSelectedTimes = [];
    } else {
      // Check if we already have a slot for today
      const hasSlotForToday = selectedTimes.length > 0;

      if (hasSlotForToday) {
        // Replace the existing slot for today
        newSelectedTimes = [time];
      } else {
        // Check total selections across all days
        const totalSlots = selectedTimeSlots.length;

        // If we already have 3 total slots across all days, show toast and don't allow more
        if (totalSlots >= 3) {
          toast({
            title: translate(language, "booking.maxSlotsReached"),
            variant: "destructive",
          });
          return;
        }

        // Add new selection for current date
        newSelectedTimes = [time];
      }
    }

    setSelectedTimes(newSelectedTimes);
    // Pass whether this slot is from external system (not from opening_hours)
    // Check if the selected time actually exists in external slots for this date
    const selectedTime = newSelectedTimes[0];
    const isTimeFromExternalSlot =
      rawExternalSlotsForDate !== null &&
      rawExternalSlotsForDate.includes(selectedTime);
    onTimeSelect(newSelectedTimes, {
      isFromExternalSlot: isTimeFromExternalSlot,
    });
  };

  // Determine if we can continue based on selection count
  // Need at least 2 and at most 3 slots selected across different days
  const canContinue =
    selectedTimeSlots.length >= 2 && selectedTimeSlots.length <= 3;

  const handleContinue = () => {
    if (!canContinue) {
      console.log("TimeSlotGrid: Cannot continue - conditions not met");
      return;
    }

    setCurrentStep("details");
    // Scroll to top on navigation to details step (time->details has form loading)
    scrollToTopDelayed(50);
  };

  const orderSummaryTranslations = {
    orderSummary: translate(language, "orderSummary.title"),
    scan: translate(language, "orderSummary.scan"),
    addOns: translate(language, "orderSummary.addOns"),
    bloodTests: translate(language, "orderSummary.bloodTests"),
    location: translate(language, "orderSummary.location"),
    timeSlots: translate(language, "orderSummary.timeSlots"),
    totalPrice: translate(language, "orderSummary.totalPrice"),
    continue: translate(language, "common.next"),
    back: translate(language, "common.prev"),
    remove: translate(language, "common.remove"),
  };

  // Calculate total price if not provided, using member-effective prices so
  // the collapsed summary bar matches the per-item discounted prices.
  const calculatedTotalPrice =
    totalPrice ||
    [...selectedProductDetails, ...selectedAddOnDetails].reduce(
      (sum, product) => sum + getEffectivePrice(product),
      0,
    );

  return (
    <div className="space-y-6 pb-1">
      <TimeSlotsDisplay
        availableSlots={slotsToDisplay}
        selectedTimes={selectedTimes}
        onTimeSelect={handleTimeSelect}
        language={language}
        bookingType="request"
        fastBookingSlots={fastSlots}
        allSelectedTimeSlots={selectedTimeSlots}
        date={date}
        hideTitle={hideTitle}
        preferenceOverride={preference}
        allowSelectionOverride={allowSelectionOverride}
        externalSlots={rawExternalSlotsForDate || []}
        showSlotIndicators={showSlotIndicators}
      />

      {/* Expandable Order Summary Footer */}
      <ExpandableOrderSummary
        totalPrice={calculatedTotalPrice}
        selectedProducts={
          selectedProductDetails as Parameters<
            typeof ExpandableOrderSummary
          >[0]["selectedProducts"]
        }
        selectedAddOns={
          selectedAddOnDetails as Parameters<
            typeof ExpandableOrderSummary
          >[0]["selectedAddOns"]
        }
        selectedClinic={currentClinic}
        selectedTimeSlots={selectedTimeSlots}
        language={language}
        onContinue={handleContinue}
        onBack={onBack}
        showBackButton={true}
        showContinueButton={true}
        continueDisabled={!canContinue}
        backDisabled={!canGoBack}
        onProductRemove={onProductRemove}
        onAddOnRemove={onAddOnRemove}
        onEditProducts={() => setCurrentStep("product")}
        onEditAddOns={() => setCurrentStep("addons")}
        onEditLocation={() => setCurrentStep("clinic")}
        onEditTimeSlots={() => setCurrentStep("date")}
        translations={orderSummaryTranslations}
        currentStep="time"
      />
    </div>
  );
};

export default TimeSlotGrid;
